import gsap, { Draggable, InertiaPlugin } from '@lib/gsap'
import { onView, onDestroy } from './_'
import { prepLineDraw, playLineDraw, cleanLineDraw } from '@utils/line-draw'

export default function (element, dataset = {}) {
  // --- SVG line draw ---
  const prepared = prepLineDraw(element)

  if (prepared) {
    let drawn = false

    onView(element, {
      threshold: 0.25,
      autoStart: true,
      callback: ({ isIn }) => {
        if (isIn && !drawn) {
          drawn = true
          playLineDraw(prepared)
        }
      },
    })

    onDestroy(() => {
      cleanLineDraw(prepared.path)
    })
  }

  /**
   * GSAP Rotate Slider
   *
   * Required data attributes on the wrapper:
   *   data-gsap-slider-init            — marks the element for initialisation
   *   data-gsap-slider-rotate="12"     — degrees between each slide
   *
   * Optional:
   *   data-gsap-slider-gap="102"       — px spacing added to card width per step
   *   data-gsap-slider-offset-y-in-view="64" — Y depth at 1 slide away (parabolic arc)
   *   data-gsap-slider-arc-power="2"   — exponent of the arc curve (2 = quadratic)
   *   data-gsap-slider-center="true"   — centre the active slide
   *   data-gsap-slider-loop="true"     — (used by the non-rotate path; ignored here)
   *
   * Inner elements:
   *   [data-gsap-slider-collection]    — drag hit-area
   *   [data-gsap-slider-list]          — container whose children are positioned
   *   [data-gsap-slider-item]          — each slide
   *   [data-gsap-slider-control="prev"] / "next" — directional nav
   *   [data-gsap-slider-control="N"]   — numbered nav button (1-based)
   *   [data-gsap-slider-active-slide]  — textContent shows current index
   *   [data-gsap-slider-total-slide]   — textContent shows total count
   *
   * Dependencies: GSAP 3, Draggable, InertiaPlugin
   */

  ;(function () {
    'use strict'

    /* ── Tunables ── */
    const DURATION = 1.2
    const DRAG_RESISTANCE = 0.025
    const THROW_RESISTANCE = 2000
    const EASE = 'expo.out'

    /* ── Helpers ── */
    const mod = (n, m) => ((n % m) + m) % m
    const parseNumber = (v) => {
      const n = parseFloat(v)
      return Number.isFinite(n) ? n : null
    }

    function debounceOnWidthChange(fn, wait) {
      let timer,
        lastWidth = window.innerWidth
      return function () {
        const newWidth = window.innerWidth
        if (newWidth === lastWidth) return
        lastWidth = newWidth
        clearTimeout(timer)
        timer = setTimeout(fn, wait)
      }
    }

    /* ── Main init ── */
    function initRotateSlider() {
      const wrapper = element.querySelector('[data-gsap-slider-init]')
      if (!wrapper) return

      if (wrapper._sliderDraggable) wrapper._sliderDraggable.kill()
      if (wrapper._sliderTimeline) wrapper._sliderTimeline.kill()

      const collection = wrapper.querySelector('[data-gsap-slider-collection]')
      const list = wrapper.querySelector('[data-gsap-slider-list]')
      const items = Array.from(wrapper.querySelectorAll('[data-gsap-slider-item]'))
      const controls = Array.from(wrapper.querySelectorAll('[data-gsap-slider-control]'))
      const totalSlideEl = wrapper.querySelector('[data-gsap-slider-total-slide]')

      if (!items.length) return

      if (totalSlideEl) {
        totalSlideEl.textContent = items.length < 10 ? '0' + items.length : String(items.length)
      }

      const rotateDeg = parseFloat(wrapper.getAttribute('data-gsap-slider-rotate')) || 0
      if (rotateDeg <= 0) return

      const styles = getComputedStyle(wrapper)
      const sliderStatus = styles.getPropertyValue('--slider-status').trim()
      let spv = parseFloat(styles.getPropertyValue('--slider-spv'))

      const itemRect = items[0].getBoundingClientRect()
      const itemWidth = itemRect.width
      const itemHeight = itemRect.height

      const gapPx =
        parseFloat(wrapper.getAttribute('data-gsap-slider-gap')) ||
        parseFloat(styles.getPropertyValue('--rotate-slider-gap')) ||
        0

      // Y depth at delta=1 (one slide away from active)
      const arcDepth =
        (parseNumber(wrapper.getAttribute('data-gsap-slider-offset-y-inview')) ??
          parseNumber(wrapper.getAttribute('data-gsap-slider-offset-y-in-view')) ??
          parseFloat(styles.getPropertyValue('--rotate-slider-offset-y-inview'))) ||
        0

      // Exponent controlling arc shape (2 = quadratic parabola, default)
      const arcPower =
        (parseNumber(wrapper.getAttribute('data-gsap-slider-arc-power')) ??
          parseFloat(styles.getPropertyValue('--rotate-slider-arc-power'))) ||
        2

      const step = itemWidth + gapPx

      if (isNaN(spv)) {
        const marginRight = parseFloat(getComputedStyle(items[0]).marginRight) || 0
        spv = collection.clientWidth / (itemWidth + marginRight + gapPx)
      }

      const visibleCount = Math.max(1, Math.min(spv, items.length))
      const visibleCeil = Math.ceil(visibleCount)

      if (!(sliderStatus === 'on' && visibleCount < items.length)) {
        wrapper.removeAttribute('data-gsap-drag-status')
        return
      }

      wrapper.setAttribute('data-gsap-drag-status', 'grab')

      const totalSlides = items.length
      const activeSlideEls = Array.from(wrapper.querySelectorAll('[data-gsap-slider-active-slide]'))

      const updateActiveText = (index) => {
        const display = index + 1
        const text = display < 10 ? '0' + display : String(display)
        activeSlideEls.forEach((el) => (el.textContent = text))
      }

      const updateControls = (activeIndex) => {
        controls.forEach((btn) => {
          const val = btn.getAttribute('data-gsap-slider-control')
          if (/^\d+$/.test(val)) {
            const idx = Math.max(0, Math.min(totalSlides - 1, parseInt(val, 10) - 1))
            btn.setAttribute('data-gsap-slider-control-status', idx === activeIndex ? 'active' : 'not-active')
          }
        })
      }

      /* ── Rotate-specific setup ── */

      gsap.set(items, { clearProps: 'position,top,left,marginRight,transform' })
      items.forEach((item) => item.removeAttribute('data-gsap-slider-item-status'))

      list.style.position = 'relative'
      list.style.height = itemHeight + 'px'

      items.forEach((item) => gsap.set(item, { xPercent: -50 }))

      const rotateSetters = items.map((item) => gsap.quickSetter(item, 'rotate', 'deg'))
      const xSetters = items.map((item) => gsap.quickSetter(item, 'x', 'px'))
      const ySetters = items.map((item) => gsap.quickSetter(item, 'y', 'px'))

      const proxy = document.createElement('div')
      gsap.set(proxy, { x: 0 })

      const getProgress = () => -gsap.getProperty(proxy, 'x') / step

      const wrapIndex = (target, current, total) =>
        target - (current - Math.round((current - target) / total) * total)

      /* ── Render ── */
      function render() {
        const progress = getProgress()

        for (let i = 0; i < totalSlides; i++) {
          const delta = wrapIndex(i, progress, totalSlides)
          const absDelta = Math.abs(delta)

          rotateSetters[i](delta * rotateDeg)
          xSetters[i](delta * step)

          // Single parabolic arc: y = arcDepth * |delta|^arcPower
          ySetters[i](arcDepth * Math.pow(absDelta, arcPower))
        }

        // Determine active / inview status
        const activeIdx = mod(Math.round(progress), totalSlides)
        const halfBefore = Math.floor((visibleCeil - 1) / 2)
        const halfAfter = visibleCeil - 1 - halfBefore

        items.forEach((item) => item.setAttribute('data-gsap-slider-item-status', 'not-active'))
        items[activeIdx].setAttribute('data-gsap-slider-item-status', 'active')

        for (let i = 1; i <= halfAfter; i++) {
          items[mod(activeIdx + i, totalSlides)].setAttribute('data-gsap-slider-item-status', 'inview')
        }
        for (let i = 1; i <= halfBefore; i++) {
          items[mod(activeIdx - i, totalSlides)].setAttribute('data-gsap-slider-item-status', 'inview')
        }

        updateActiveText(activeIdx)
        updateControls(activeIdx)
      }

      /* ── Wire up controls ── */
      controls.forEach((btn) => {
        btn.disabled = false
        btn.setAttribute('data-gsap-slider-control-status', 'active')

        const val = btn.getAttribute('data-gsap-slider-control')

        if (val === 'next' || val === 'prev') {
          const dir = val === 'next' ? 1 : -1
          btn.onclick = () => {
            gsap.killTweensOf(proxy)
            const current = getProgress()
            const targetX = -(Math.round(current) + dir) * step
            gsap.to(proxy, { x: targetX, duration: DURATION, ease: EASE, onUpdate: render })
          }
        } else if (/^\d+$/.test(val)) {
          const targetIdx = Math.max(0, Math.min(totalSlides - 1, parseInt(val, 10) - 1))
          btn.onclick = () => {
            gsap.killTweensOf(proxy)
            const current = getProgress()
            const targetX = -(current + wrapIndex(targetIdx, current, totalSlides)) * step
            gsap.to(proxy, { x: targetX, duration: DURATION, ease: EASE, onUpdate: render })
          }
        }
      })

      /* ── Draggable ── */
      wrapper._sliderDraggable = Draggable.create(proxy, {
        type: 'x',
        trigger: collection,
        inertia: true,
        maxDuration: 1,
        minDuration: 0.5,
        dragResistance: DRAG_RESISTANCE,
        throwResistance: THROW_RESISTANCE,
        bounds: null,
        edgeResistance: 0,
        snap: (val) => Math.round(val / step) * step,
        onDrag: render,
        onThrowUpdate: render,
        onThrowComplete: render,
        onPress: () => wrapper.setAttribute('data-gsap-drag-status', 'grabbing'),
        onDragStart: () => wrapper.setAttribute('data-gsap-drag-status', 'grabbing'),
        onRelease: () => wrapper.setAttribute('data-gsap-drag-status', 'grab'),
      })[0]

      gsap.set(list, { x: 0 })
      render()

      wrapper._sliderTimeline = null
      window.addEventListener('resize', debounceOnWidthChange(initRotateSlider, 200))
    }

    /* ── Boot ── */
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initRotateSlider)
    } else {
      initRotateSlider()
    }
  })()
}
